<template>
    <div>
      <Header />
  
      <!-- Page Header -->
      <div class="page-header">
        <div>
          <h5 class="page-title">Billing Customers</h5>
          <p class="page-sub">Manage and track all billing customers</p>
        </div>
        <div class="page-actions">
  
          <div class="search-wrap">
            <font-awesome-icon icon="search" class="search-icon" />
            <input
              type="text"
              class="search-input"
              placeholder="Search customers..."
              v-model="tableData.search"
              @input="tableData.page = 1; fetchData()"
            />
          </div>
  
          <select v-model="tableData.status" @change="tableData.page = 1; fetchData()" class="filter-select">
            <option :value="[0,1]">All Statuses</option>
            <option :value="[1]">Active</option>
            <option :value="[0]">Inactive</option>
          </select>
  
          <div class="dropdown">
            <button class="btn-outline dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
              <font-awesome-icon icon="download" /> Export
            </button>
            <ul class="dropdown-menu dropdown-menu-end">
              <li>
                <a class="dropdown-item" href="#"
                  @click="drawPdf(print_content, headerArrayColumn, 'p', 'billing_customers', 'Billing Customers')">
                  <font-awesome-icon icon="file-pdf" class="me-2 text-danger" /> PDF
                </a>
              </li>
              <li>
                <a class="dropdown-item" href="#" @click="reusableMixinToCSV('billing_customers.csv')">
                  <font-awesome-icon icon="file-csv" class="me-2 text-success" /> CSV
                </a>
              </li>
            </ul>
          </div>
  
          <router-link
            :to="$url('/upload-billing-customers')"
            class="btn-outline"
            :class="permission.create_billing_group != 1 ? 'btn-outline--disabled' : ''"
          >
            <font-awesome-icon icon="upload" /> Bulk Upload
          </router-link>
  
          <button
            class="btn-primary-action"
            :class="permission.create_billing_group != 1 ? 'disabled' : ''"
            @click="openCreateModal"
          >
            <font-awesome-icon icon="plus" /> Create Customer
          </button>
  
        </div>
      </div>
  
      <!-- Data Card -->
      <div class="data-card">
  
        <!-- Loading -->
        <div v-if="loading" class="state-block">
          <div class="spinner-border text-primary" style="width:3rem;height:3rem;" role="status"></div>
          <p class="mt-3 text-muted-cell">Loading customers...</p>
        </div>
  
        <!-- 403 -->
        <div v-else-if="permission_status === 403" class="state-block">
          <div class="state-icon state-icon--red"><font-awesome-icon icon="lock" /></div>
          <p class="state-title">Access Denied</p>
          <p class="state-sub">You don't have permission to access this resource.</p>
        </div>
  
        <!-- Empty -->
        <div v-else-if="details.length === 0" class="state-block">
          <div class="state-icon state-icon--blue"><font-awesome-icon icon="users" /></div>
          <p class="state-title">No customers found</p>
          <p class="state-sub">Your customer list is empty. Create your first customer to get started.</p>
          <button
            v-if="permission.create_billing_group == 1"
            class="btn-primary-action mt-3"
            @click="openCreateModal"
          >
            <font-awesome-icon icon="plus" /> Create Customer
          </button>
        </div>
  
        <template v-else>
  
          <!-- Toolbar -->
          <div class="table-toolbar">
            <span class="table-count">
              Showing <strong>{{ pagination.from }}–{{ pagination.to }}</strong>
              of <strong>{{ pagination.total }}</strong>
            </span>
            <div class="toolbar-right">
              <label class="table-count">Rows:</label>
              <select v-model="tableData.length" @change="tableData.page = 1; fetchData()" class="rows-select">
                <option value="5">5</option>
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="30">30</option>
                <option value="50">50</option>
                <option value="100">100</option>
                <option :value="pagination.total">All</option>
              </select>
            </div>
          </div>
  
          <!-- Table -->
          <div class="table-wrap">
            <table class="data-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Customer</th>
                  <th>Phone</th>
                  <th>Address</th>
                  <th>Groups</th>
                  <th>Status</th>
                  <th class="col-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, index) in details" :key="row.id">
  
                  <td class="text-muted-cell">{{ index + 1 }}</td>
  
                  <!-- Customer name + email + avatar -->
                  <td>
                    <div class="customer-cell">
                      <div class="customer-avatar">{{ creatorInitials(row) }}</div>
                      <div>
                        <div class="customer-name">{{ row.customer_name }}</div>
                        <div class="customer-email">{{ row.email }}</div>
                      </div>
                    </div>
                  </td>
  
                  <td class="text-muted-cell">{{ row.phone_no }}</td>
  
                  <td class="text-muted-cell address-cell">{{ row.address || '—' }}</td>
  
                  <!-- Groups -->
                  <td>
                    <template v-if="row.groups && row.groups.length">
                      <span
                        v-for="grp in row.groups"
                        :key="grp.id"
                        class="group-tag"
                      >{{ grp?.group?.name }}</span>
                    </template>
                    <span v-else class="text-muted-cell">—</span>
                  </td>
  
                  <!-- Toggle status -->
                  <td>
                    <label class="toggle-switch">
                      <input
                        type="checkbox"
                        :true-value="1"
                        :false-value="0"
                        v-model="statuses[index]"
                        @change="updateCustomerStatus(row.id)"
                      />
                      <span class="toggle-track">
                        <span class="toggle-thumb"></span>
                      </span>
                      <span class="toggle-label" :class="statuses[index] ? 'on' : 'off'">
                        {{ statuses[index] ? 'Active' : 'Inactive' }}
                      </span>
                    </label>
                  </td>
  
                  <!-- Actions -->
                  <td class="col-actions">
                    <div class="actions-cell">
                      <button
                        class="action-btn action-btn--edit"
                        :class="permission.update_billing_group != 1 ? 'action-btn--disabled' : ''"
                        title="Edit"
                        @click="openUpdateModal(index)"
                      >
                        <font-awesome-icon icon="pen" />
                      </button>
                      <button
                        class="action-btn action-btn--delete"
                        :class="permission.delete_billing_group != 1 ? 'action-btn--disabled' : ''"
                        title="Delete"
                        @click="openDeleteModal(index)"
                      >
                        <font-awesome-icon icon="trash" />
                      </button>
                    </div>
                  </td>
  
                </tr>
              </tbody>
            </table>
          </div>
  
          <!-- Pagination -->
          <div class="table-footer">
            <pagination
              :pagination="pagination"
              @prev="tableData.page = pagination.currentPage - 1; fetchData()"
              @next="tableData.page = pagination.currentPage + 1; fetchData()"
            />
          </div>
  
        </template>
      </div>
  
  
      <!-- ── CREATE MODAL ── -->
      <div class="modal fade" id="create_customer" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered">
          <div class="modal-content modal-styled">
            <div class="modal-header modal-header-styled">
              <h5 class="modal-title-styled">Create New Customer</h5>
              <button type="button" class="modal-close-btn" @click="closeCreateModal" aria-label="Close">
                <font-awesome-icon icon="times" />
              </button>
            </div>
            <form @submit.prevent="createCustomer">
              <div class="modal-body modal-body-styled">
                <div class="form-group-styled">
                  <label class="form-label-styled">Billing Group <span class="req">*</span></label>
                  <Multiselect
                    v-model="customer.groups"
                    label="name"
                    :options="group_arr"
                    placeholder="Select billing group(s)"
                    mode="tags"
                    :searchable="true"
                    :create-option="false"
                    :close-on-select="false"
                  />
                </div>
                <div class="form-group-styled">
                  <label class="form-label-styled">Customer Name <span class="req">*</span></label>
                  <input type="text" class="form-input-styled" placeholder="Enter customer name" v-model="customer.customer_name" required />
                </div>
                <div class="form-group-styled">
                  <label class="form-label-styled">Email <span class="req">*</span></label>
                  <input type="email" class="form-input-styled" placeholder="Enter customer email" v-model="customer.email" required />
                </div>
                <div class="form-group-styled">
                  <label class="form-label-styled">Phone Number <span class="req">*</span></label>
                  <input type="text" class="form-input-styled" placeholder="Enter phone number" v-model="customer.phone_no" required />
                </div>
                <div class="form-group-styled" style="margin-bottom:0;">
                  <label class="form-label-styled">Address</label>
                  <input type="text" class="form-input-styled" placeholder="Enter address" v-model="customer.address" />
                </div>
              </div>
              <div class="modal-footer modal-footer-styled">
                <button type="button" class="btn-ghost" @click="closeCreateModal">Cancel</button>
                <button type="submit" class="btn-primary-action" :disabled="loading">
                  <span v-if="!loading">Create Customer</span>
                  <span v-else>Processing...</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
  
  
      <!-- ── UPDATE MODAL ── -->
      <div class="modal fade" id="update_modal" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered">
          <div class="modal-content modal-styled">
            <div class="modal-header modal-header-styled">
              <h5 class="modal-title-styled">Update Customer</h5>
              <button type="button" class="modal-close-btn" @click="closeUpdateModal" aria-label="Close">
                <font-awesome-icon icon="times" />
              </button>
            </div>
            <form @submit.prevent="updateCustomer">
              <div class="modal-body modal-body-styled">
                <div class="form-group-styled">
                  <label class="form-label-styled">Billing Group <span class="req">*</span></label>
                  <Multiselect
                    v-model="groups"
                    label="name"
                    :options="group_arr"
                    placeholder="Select billing group(s)"
                    mode="tags"
                    :searchable="true"
                    :object="true"
                    :close-on-select="false"
                  />
                </div>
                <div class="form-group-styled">
                  <label class="form-label-styled">Customer Name <span class="req">*</span></label>
                  <input type="text" class="form-input-styled" placeholder="Enter customer name" v-model="currentData.customer_name" required />
                </div>
                <div class="form-group-styled">
                  <label class="form-label-styled">Email <span class="req">*</span></label>
                  <input type="email" class="form-input-styled" placeholder="Enter customer email" v-model="currentData.email" required />
                </div>
                <div class="form-group-styled">
                  <label class="form-label-styled">Phone Number <span class="req">*</span></label>
                  <input type="text" class="form-input-styled" placeholder="Enter phone number" v-model="currentData.phone_no" required />
                </div>
                <div class="form-group-styled" style="margin-bottom:0;">
                  <label class="form-label-styled">Address</label>
                  <input type="text" class="form-input-styled" placeholder="Enter address" v-model="currentData.address" />
                </div>
              </div>
              <div class="modal-footer modal-footer-styled">
                <button type="button" class="btn-ghost" @click="closeUpdateModal">Cancel</button>
                <button type="submit" class="btn-primary-action" :disabled="loading">
                  <span v-if="!loading">Save Changes</span>
                  <span v-else>Processing...</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
  
  
      <!-- ── DELETE MODAL ── -->
      <div class="modal fade" id="delete_modal" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered">
          <div class="modal-content modal-styled">
            <div class="modal-body modal-body-styled" style="padding:32px 24px 24px; text-align:center;">
              <div class="delete-icon-wrap">
                <font-awesome-icon icon="trash" style="font-size:26px;" />
              </div>
              <div class="delete-title">Delete Customer</div>
              <p class="delete-sub">
                Are you sure you want to delete
                <strong>{{ currentData.customer_name }}</strong>?
                This action cannot be undone.
              </p>
            </div>
            <div class="modal-footer modal-footer-styled" style="justify-content:center; gap:12px;">
              <button type="button" class="btn-ghost" @click="closeDeleteModal">Cancel</button>
              <button type="button" class="btn-danger-action" @click="deleteGroup">
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      </div>
  
    </div>
  </template>
  
  <script>
  import { reusableMixin } from '../../../lib/reusableMixin';
  import Header from './component/header.vue';
  import axios from 'axios';
  import { useToast } from 'vue-toastification';
  import { useStore } from 'vuex';
  import Multiselect from '@vueform/multiselect';
  
  export default {
    name: 'BillingCustomers',
  
    components: { Header, Multiselect },
  
    mixins: [reusableMixin],
  
    data() {
      return {
        print_content: [],
        headerArrayColumn: ['S/N', 'Customer Name', 'Email', 'Phone Number', 'Address', 'Status'],
  
        details: [],
        statuses: [],
        loading: false,
        permission_status: null,
  
        currentData: {},
        group_arr: [],
        groups: [],
  
        customer: {
          groups: [],
          customer_name: '',
          email: '',
          phone_no: '',
          address: '',
        },
  
        tableData: { page: 1,
          draw: 0,
          length: 5,
          search: '',
          column: 0,
          dir: 'desc',
          status: [0, 1],
        },
  
        pagination: {
          lastPage: '', currentPage: '', total: '',
          lastPageUrl: '', nextPageUrl: '', prevPageUrl: '',
          from: '', to: '',
        },
  
        toast: useToast(),
        store: useStore(),
      };
    },
  
    computed: {
      permission() {
        return this.store.state.getCurrentUserPermission;
      },
    },
  
    created() {
      this.fetchData();
      this.fetchActiveGroups();
    },
  
    methods: {
      // ── Helpers ────────────────────────────────────────────────────────
      creatorInitials(row) {
        const name = row.customer_name ?? '';
        return name.split(' ').map(p => p[0] ?? '').join('').substring(0, 2).toUpperCase();
      },
  
      dataToPrint(details) {
        this.print_content = details.map((d, idx) => ({
          sn:            idx + 1,
          customer_name: d.customer_name,
          email:         d.email,
          phone_no:      d.phone_no,
          address:       d.address,
          status:        d.is_active ? 'Active' : 'Inactive',
        }));
      },
  
      configPagination(data) {
        this.pagination.lastPage    = data.last_page;
        this.pagination.currentPage = data.current_page;
        this.pagination.total       = data.total;
        this.pagination.lastPageUrl = data.last_page_url;
        this.pagination.nextPageUrl = data.next_page_url;
        this.pagination.prevPageUrl = data.prev_page_url;
        this.pagination.from        = data.from;
        this.pagination.to          = data.to;
      },
  
      // ── Fetch ───────────────────────────────────────────────────────────
      async fetchData(url = '/billing/customer/all') {
        this.loading = true;
        this.tableData.draw++;
        try {
          const { data } = await axios.get(url, { params: this.tableData });
          this.details = data.data.data;
          this.statuses = this.details.map(c => c.is_active);
          this.dataToPrint(this.details);
          this.configPagination(data.data);
        } catch (error) {
          if (error?.response?.status === 403) {
            this.permission_status = 403;
            this.toast.error(error.response.data.message);
          }
        } finally {
          this.loading = false;
        }
      },
  
      async fetchActiveGroups() {
        try {
          const { data } = await axios.get('/billing/group/active');
          this.group_arr = data.data.map(g => ({ value: g.id, name: g.name }));
        } catch (error) {
            console.log(error.response?.data?.message);
        }
      },
  
      // ── Create ──────────────────────────────────────────────────────────
      openCreateModal() {
        this.customer = { groups: [], customer_name: '', email: '', phone_no: '', address: '' };
        window.$('#create_customer').modal('show');
      },
  
      async createCustomer() {
        this.loading = true;
        try {
          await axios.post('/billing/customer/create', this.customer);
          this.toast.success('Customer created successfully!');
          this.fetchData();
          window.$('#create_customer').modal('hide');
        } catch (error) {
          this.toast.warning(error.response?.data?.message ?? 'Could not create customer.');
        } finally {
          this.loading = false;
        }
      },
  
      // ── Update ──────────────────────────────────────────────────────────
      openUpdateModal(index) {
        this.currentData = { ...this.details[index] };
        this.groups = this.currentData.groups?.map(st => ({
          value: st.billing_group_id,
          name:  st?.group?.name,
        })) ?? [];
        window.$('#update_modal').modal('show');
      },
  
      closeCreateModal() {
        window.$('#create_customer').modal('hide');
      },
  
      closeUpdateModal() {
        window.$('#update_modal').modal('hide');
      },
  
      closeDeleteModal() {
        window.$('#delete_modal').modal('hide');
      },
  
      async updateCustomer() {
        this.loading = true;
        try {
          await axios.put('/billing/customer/update', {
            id:            this.currentData.id,
            customer_name: this.currentData.customer_name,
            email:         this.currentData.email,
            phone_no:      this.currentData.phone_no,
            address:       this.currentData.address,
            groups:        this.groups,
          });
          this.toast.success('Customer updated successfully!');
          this.fetchData();
          this.closeUpdateModal();
        } catch (error) {
          const status = error.response?.status;
          if (status === 422) this.toast.warning('Please check your input and try again.');
          else if (status === 500) this.toast.error('Server error. Please try again.');
          else this.toast.error(error.response?.data?.message ?? 'Update failed.');
        } finally {
          this.loading = false;
        }
      },
  
      // ── Status toggle ────────────────────────────────────────────────────
      async updateCustomerStatus(id) {
        try {
          const res = await axios.put(`billing/customer/update-status/${id}`);
          this.toast.success(res.data.message);
          this.fetchData();
        } catch (error) {
          const status = error.response?.status;
          if (status === 422) this.toast.warning('Invalid request.');
          else if (status === 500) this.toast.error('Server error. Please try again.');
        }
      },
  
      // ── Delete ───────────────────────────────────────────────────────────
      openDeleteModal(index) {
        this.currentData = { ...this.details[index] };
        window.$('#delete_modal').modal('show');
      },
  
      async deleteGroup() {
        try {
          await axios.delete(`/billing/customer/delete/${this.currentData.id}`);
          this.toast.success('Customer deleted successfully.');
          this.fetchData();
          window.$('#delete_modal').modal('hide');
        } catch (error) {
          const msg = error.response?.data?.message ?? 'Customer could not be deleted.';
          this.toast.error(msg);
        }
      },
    },
  };
  </script>
  
  <style scoped>
  /* ── Layout ─────────────────────────────────────────────────────────── */
  .page-header  { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:14px; margin-bottom:20px; }
  .page-title   { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
  .page-sub     { font-size:13px; color:#7a90b0; margin:0; }
  .page-actions { display:flex; align-items:center; flex-wrap:wrap; gap:8px; }
  
  /* ── Search ─────────────────────────────────────────────────────────── */
  .search-wrap  { position:relative; display:flex; align-items:center; }
  .search-icon  { position:absolute; left:11px; color:#7a90b0; font-size:13px; pointer-events:none; }
  .search-input { padding:8px 12px 8px 32px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; width:210px; outline:none; transition:border-color .15s; }
  .search-input:focus { border-color:#004AAD; }
  .filter-select { padding:8px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; outline:none; cursor:pointer; }
  
  /* ── Buttons ─────────────────────────────────────────────────────────── */
  .btn-primary-action { display:inline-flex; align-items:center; gap:7px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:8px 16px; border-radius:8px; border:none; text-decoration:none; cursor:pointer; transition:background .15s; white-space:nowrap; }
  .btn-primary-action:hover { background:#003b8e; color:#fff; }
  .btn-primary-action:disabled, .btn-primary-action.disabled { opacity:.5; pointer-events:none; }
  .btn-outline { display:inline-flex; align-items:center; gap:7px; background:#fff; color:#1a2b45; border:1px solid #dde5f0; font-size:13px; font-weight:500; padding:8px 14px; border-radius:8px; cursor:pointer; transition:background .15s; white-space:nowrap; text-decoration:none; }
  .btn-outline:hover { background:#f4f7fb; color:#1a2b45; text-decoration:none; }
  .btn-outline--disabled { opacity:.5; pointer-events:none; }
  .btn-ghost { display:inline-flex; align-items:center; gap:7px; background:#f4f7fb; color:#3d5068; border:1px solid #dde5f0; font-size:13px; font-weight:600; padding:8px 18px; border-radius:8px; border:none; cursor:pointer; transition:background .15s; }
  .btn-ghost:hover { background:#e4eaf2; }
  .btn-danger-action { display:inline-flex; align-items:center; gap:7px; background:#e03535; color:#fff; font-size:13px; font-weight:600; padding:8px 20px; border-radius:8px; border:none; cursor:pointer; transition:background .15s; }
  .btn-danger-action:hover { background:#c02828; }
  
  /* ── Data Card ───────────────────────────────────────────────────────── */
  .data-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
  
  /* ── States ──────────────────────────────────────────────────────────── */
  .state-block  { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:64px 24px; text-align:center; }
  .state-icon   { width:60px; height:60px; border-radius:16px; display:flex; align-items:center; justify-content:center; font-size:24px; margin-bottom:16px; }
  .state-icon--blue { background:rgba(0,74,173,.1); color:#004AAD; }
  .state-icon--red  { background:#fff0f0; color:#e03535; }
  .state-title  { font-size:16px; font-weight:700; color:#1a2b45; margin:0 0 6px; }
  .state-sub    { font-size:13px; color:#7a90b0; margin:0; }
  .text-muted-cell { color:#4a5e78; }
  .mt-3 { margin-top:12px; }
  
  /* ── Table Toolbar ───────────────────────────────────────────────────── */
  .table-toolbar  { display:flex; align-items:center; justify-content:space-between; padding:14px 20px; border-bottom:1px solid #e4eaf2; flex-wrap:wrap; gap:10px; }
  .table-count    { font-size:13px; color:#7a90b0; }
  .table-count strong { color:#1a2b45; }
  .toolbar-right  { display:flex; align-items:center; gap:8px; }
  .rows-select    { border:1px solid #dde5f0; border-radius:7px; padding:5px 10px; font-size:13px; color:#1a2b45; background:#f8fafc; outline:none; cursor:pointer; }
  
  /* ── Table ───────────────────────────────────────────────────────────── */
  .table-wrap   { overflow-x:auto; }
  .data-table   { width:100%; border-collapse:collapse; }
  .data-table thead tr { background:#f8fafc; }
  .data-table thead th { font-size:11.5px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; padding:12px 16px; border-bottom:1px solid #e4eaf2; white-space:nowrap; }
  .data-table tbody tr { border-bottom:1px solid #f0f4f8; transition:background .12s; }
  .data-table tbody tr:last-child { border-bottom:none; }
  .data-table tbody tr:hover { background:#f8fafc; }
  .data-table tbody td { padding:12px 16px; font-size:13px; vertical-align:middle; }
  .col-actions { width:90px; text-align:center; }
  .address-cell { max-width:160px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
  
  /* ── Customer cell ───────────────────────────────────────────────────── */
  .customer-cell   { display:flex; align-items:center; gap:10px; }
  .customer-avatar { width:32px; height:32px; border-radius:50%; background:rgba(0,74,173,.1); color:#004AAD; display:flex; align-items:center; justify-content:center; font-size:11px; font-weight:700; flex-shrink:0; }
  .customer-name   { font-size:13px; font-weight:600; color:#1a2b45; }
  .customer-email  { font-size:12px; color:#7a90b0; margin-top:1px; }
  
  /* ── Group tags ──────────────────────────────────────────────────────── */
  .group-tag { display:inline-block; background:rgba(0,74,173,.08); color:#004AAD; border:1px solid rgba(0,74,173,.18); border-radius:5px; padding:2px 8px; font-size:11.5px; font-weight:600; margin:2px 3px 2px 0; }
  
  /* ── Toggle switch ───────────────────────────────────────────────────── */
  .toggle-switch { display:inline-flex; align-items:center; gap:8px; cursor:pointer; user-select:none; }
  .toggle-switch input { display:none; }
  .toggle-track  { position:relative; width:36px; height:20px; border-radius:20px; background:#d1d9e8; transition:background .2s; flex-shrink:0; }
  .toggle-switch input:checked + .toggle-track { background:#16a34a; }
  .toggle-thumb  { position:absolute; top:3px; left:3px; width:14px; height:14px; border-radius:50%; background:#fff; box-shadow:0 1px 3px rgba(0,0,0,.2); transition:transform .2s; }
  .toggle-switch input:checked + .toggle-track .toggle-thumb { transform:translateX(16px); }
  .toggle-label  { font-size:12px; font-weight:600; }
  .toggle-label.on  { color:#16a34a; }
  .toggle-label.off { color:#7a90b0; }
  
  /* ── Action buttons ──────────────────────────────────────────────────── */
  .actions-cell { display:flex; align-items:center; justify-content:center; gap:6px; }
  .action-btn   { width:30px; height:30px; border-radius:7px; border:1px solid #dde5f0; background:#fff; display:flex; align-items:center; justify-content:center; font-size:12px; color:#7a90b0; cursor:pointer; transition:all .15s; }
  .action-btn--edit:hover   { background:#e8f4fd; color:#004AAD; border-color:#c5d6f5; }
  .action-btn--delete:hover { background:#fff0f0; color:#e03535; border-color:#f5c6c6; }
  .action-btn--disabled     { opacity:.35; pointer-events:none; }
  
  /* ── Table footer ────────────────────────────────────────────────────── */
  .table-footer { padding:14px 20px; border-top:1px solid #e4eaf2; display:flex; justify-content:flex-end; }
  
  /* ── Dropdowns ───────────────────────────────────────────────────────── */
  .dropdown-menu { border:1px solid #e4eaf2; border-radius:9px; box-shadow:0 8px 24px rgba(0,0,0,.08); padding:6px; min-width:170px; }
  .dropdown-item { border-radius:6px; font-size:13px; padding:8px 12px; color:#1a2b45; display:flex; align-items:center; }
  .dropdown-item:hover { background:#f4f7fb; }
  .me-2 { margin-right:8px; }
  
  /* ── Modals ──────────────────────────────────────────────────────────── */
  .modal-styled       { border-radius:14px; border:none; box-shadow:0 20px 60px rgba(0,0,0,.15); overflow:hidden; }
  .modal-header-styled{ padding:20px 24px 16px; border-bottom:1px solid #e4eaf2; display:flex; align-items:center; justify-content:space-between; }
  .modal-title-styled { font-size:16px; font-weight:700; color:#1a2b45; margin:0; }
  .modal-close-btn    { width:30px; height:30px; border-radius:7px; border:1px solid #dde5f0; background:transparent; cursor:pointer; display:flex; align-items:center; justify-content:center; color:#7a90b0; font-size:13px; transition:background .15s; }
  .modal-close-btn:hover { background:#f4f7fb; }
  .modal-body-styled  { padding:20px 24px; }
  .modal-footer-styled{ padding:16px 24px; border-top:1px solid #e4eaf2; display:flex; justify-content:flex-end; gap:10px; }
  
  /* ── Form elements ───────────────────────────────────────────────────── */
  .form-group-styled  { margin-bottom:16px; }
  .form-label-styled  { display:block; font-size:13px; font-weight:600; color:#1a2b45; margin-bottom:6px; }
  .req { color:#e03535; }
  .form-input-styled  { width:100%; padding:9px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; outline:none; transition:border-color .15s, box-shadow .15s; }
  .form-input-styled:focus { border-color:#004AAD; box-shadow:0 0 0 3px rgba(0,74,173,.08); }
  
  /* ── Delete modal ────────────────────────────────────────────────────── */
  .delete-icon-wrap { width:64px; height:64px; border-radius:50%; background:#fff0f0; color:#e03535; display:flex; align-items:center; justify-content:center; margin:0 auto 16px; }
  .delete-title { font-size:17px; font-weight:700; color:#1a2b45; margin-bottom:8px; }
  .delete-sub   { font-size:13px; color:#7a90b0; line-height:1.5; }
  
  /* ── Responsive ──────────────────────────────────────────────────────── */
  @media (max-width: 991px) {
    .page-header, .page-actions { flex-direction:column; align-items:stretch; }
    .search-input { width:100%; }
  }
  </style>